import { NetworkStatus, useQuery } from "@apollo/client";
import { useMemo, useState } from "react";

import { EventsApiResponse } from "../../../../common/types/events/api";
import { GetRecentEventsParams } from "../../../../common/types/events/params";
import { buildEventQueryVariables } from "../../../../common/utils/buildEventQueryVariables";
import { handleApolloError } from "../../../../common/utils/handleApolloError";
import { errorNotification } from "../../../../common/variables/notification";
import { GET_RECENT_EVENTS } from "../../../../rest/queries";
import { mapEventWithRecording } from "../../adapters/eventWithRecordingAdapter";
import { EventWithRecordingViewModel } from "../../models/eventWithRecordingViewModel";

export const useGetRecentEvents = (params: GetRecentEventsParams) => {
  const skip = !params.customerId;

  const [fetchMoreError, setFetchMoreError] = useState(false);

  const variables = useMemo(
    () => buildEventQueryVariables(params),
    [
      params.customerId,
      params.modelId,
      params.facilityId,
      params.deviceId,
      params.fromDate,
      params.toDate,
      params.pageSize,
    ]
  );

  const { data, loading, error, fetchMore, networkStatus } = useQuery<{
    events: EventsApiResponse;
  }>(GET_RECENT_EVENTS, {
    variables,
    skip,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
    onError: handleApolloError,
  });

  const events = useMemo<EventWithRecordingViewModel[]>(() => {
    const mapped = data?.events?.data.map(mapEventWithRecording) ?? [];

    return mapped.slice().sort((a, b) => {
      const aTime = new Date(
        a.newestRecording?.timestamp ?? a.event.timestamp
      ).getTime();
      const bTime = new Date(
        b.newestRecording?.timestamp ?? b.event.timestamp
      ).getTime();

      return bTime - aTime; // newest first
    });
  }, [data]);

  const lastEvaluatedKey = data?.events?.lastEvaluatedKey;

  const fetchNextPage = async () => {
    if (!lastEvaluatedKey || fetchMoreLoading || fetchMoreError) return;

    try {
      await fetchMore({
        variables: {
          ...variables,
          lastEvaluatedKey: encodeURIComponent(
            JSON.stringify(lastEvaluatedKey)
          ),
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          setFetchMoreError(false);

          return {
            ...fetchMoreResult,
            events: {
              ...fetchMoreResult.events,
              data: [
                ...(prev.events?.data ?? []),
                ...(fetchMoreResult.events?.data ?? []),
              ],
              lastEvaluatedKey: fetchMoreResult.events?.lastEvaluatedKey,
            },
          };
        },
      });
    } catch (error) {
      setFetchMoreError(true);

      errorNotification("Error fetching next page of events");

      console.error("Error fetching next page of events:", error);
    }
  };

  const fetchMoreLoading = networkStatus === NetworkStatus.fetchMore;

  return {
    events,
    loading: loading && !fetchMoreLoading,
    error,
    fetchMore,
    fetchNextPage,
    lastEvaluatedKey,
    fetchMoreLoading,
    networkStatus,
  } as const;
};
